import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Spinner, Alert, Nav } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Tool Analytics Component
 * Displays spending and usage statistics
 */
const ToolAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('production'); // 'production' or 'test'

  useEffect(() => {
    fetchAnalytics();
  }, [viewMode]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const dryRun = viewMode === 'test';
      const response = await fetch(`/api/admin/tools/analytics?days=30&dryRun=${dryRun}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setAnalytics(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch analytics');
      setLoading(false);
    }
  };

  const formatCost = (cost) => {
    return '$' + parseFloat(cost || 0).toFixed(4);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  if (error) {
    return (
      <AdminToolsLayout>
        <Alert variant="danger">{error}</Alert>
      </AdminToolsLayout>
    );
  }

  const { dailySpending, totalStats, modelUsage, statusCounts } = analytics;

  return (
    <AdminToolsLayout>
      {/* View Mode Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link
            active={viewMode === 'production'}
            onClick={() => setViewMode('production')}
            style={{ cursor: 'pointer' }}
          >
            Production Data
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={viewMode === 'test'}
            onClick={() => setViewMode('test')}
            style={{ cursor: 'pointer' }}
          >
            Test/Mock Data
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {viewMode === 'test' && (
        <Alert variant="info" className="mb-4">
          Showing data from dry run/test mode. These are mock API calls with simulated costs.
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="mb-0">{totalStats?.totalArticles || 0}</h3>
              <small className="text-muted">Total Articles</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="mb-0">{formatCost(totalStats?.totalCost)}</h3>
              <small className="text-muted">Total Spending</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="mb-0">{formatCost(totalStats?.avgCost)}</h3>
              <small className="text-muted">Avg. Cost/Article</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status Breakdown */}
      {statusCounts && statusCounts.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Articles by Status</h6>
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap gap-4">
              {statusCounts.map((status) => (
                <div key={status.status}>
                  <strong>{status.count}</strong>{' '}
                  <span className="text-muted">{status.status}</span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Daily Spending */}
      {dailySpending && dailySpending.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Daily Spending (Last 30 Days)</h6>
          </Card.Header>
          <Card.Body>
            <Table responsive size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Requests</th>
                  <th>Total Spent</th>
                </tr>
              </thead>
              <tbody>
                {dailySpending.slice(-14).reverse().map((day) => (
                  <tr key={day.spending_date}>
                    <td>{formatDate(day.spending_date)}</td>
                    <td>{day.request_count}</td>
                    <td>{formatCost(day.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Model Usage */}
      {modelUsage && modelUsage.length > 0 && (
        <Card>
          <Card.Header>
            <h6 className="mb-0">Model Usage by Type</h6>
          </Card.Header>
          <Card.Body>
            <Table responsive size="sm">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Model</th>
                  <th>Uses</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {modelUsage.map((model, idx) => {
                  const typeLabels = { text: 'Text', audio: 'Audio', image: 'Image', other: 'Other' };
                  const typeBadgeColors = { text: '#e8f0fe', audio: '#fef7e0', image: '#e6f4ea', other: '#f1f3f5' };
                  const typeTextColors = { text: '#1a73e8', audio: '#b06000', image: '#137333', other: '#495057' };
                  return (
                    <tr key={`${model.model_used}-${idx}`}>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '0.2rem 0.5rem',
                          backgroundColor: typeBadgeColors[model.model_type] || '#f1f3f5',
                          color: typeTextColors[model.model_type] || '#495057',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {typeLabels[model.model_type] || model.model_type}
                        </span>
                      </td>
                      <td><code style={{ fontSize: '0.8rem' }}>{model.model_used || 'Unknown'}</code></td>
                      <td>{model.count}</td>
                      <td>{formatCost(model.totalCost)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </AdminToolsLayout>
  );
};

export default ToolAnalytics;
